import { useEffect, useRef } from 'react'
import { useChurch } from './useChurch'
import { generatePWAManifest, updatePWAMetaTags, updatePageTitle } from '@/lib/pwa'

/**
 * Hook para gerenciar PWA dinamicamente com dados da igreja
 */
export function usePWA(pageTitle?: string) {
  const { church, loading } = useChurch()
  const lastChurchRef = useRef<any>(null)
  const lastPageTitleRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!loading && church) {
      // Verificar se os dados mudaram para evitar atualizações desnecessárias
      const churchChanged = JSON.stringify(church) !== JSON.stringify(lastChurchRef.current)
      const pageTitleChanged = pageTitle !== lastPageTitleRef.current

      if (churchChanged || pageTitleChanged) {
        console.log('🏛️ Dados da igreja para PWA:', church)
        console.log('🖼️ Logo da igreja:', church?.logo_light)
        
        // Atualizar título da página apenas se mudou
        if (pageTitleChanged) {
          updatePageTitle(church, pageTitle)
          lastPageTitleRef.current = pageTitle
        }
        
        // Atualizar meta tags PWA e Open Graph apenas se a igreja mudou
        if (churchChanged) {
          updatePWAMetaTags(church)
          
          // Salvar dados da igreja no localStorage para o manifesto
          localStorage.setItem('church-data', JSON.stringify(church))
          
          // Gerar e atualizar o manifesto dinamicamente
          updateManifest()
          
          lastChurchRef.current = church
        }
      }
    }
  }, [church, loading, pageTitle])

  const updateManifest = () => {
    try {
      // Gerar novo manifesto
      const manifest = generatePWAManifest(church)
      
      // Criar blob com o manifesto
      const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], {
        type: 'application/json'
      })
      
      // Criar URL para o blob
      const manifestUrl = URL.createObjectURL(manifestBlob)
      
      // Atualizar ou criar link do manifesto
      let manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement
      if (manifestLink) {
        // Revogar URL anterior se existir
        if (manifestLink.href.startsWith('blob:')) {
          URL.revokeObjectURL(manifestLink.href)
        }
        manifestLink.href = manifestUrl
      } else {
        manifestLink = document.createElement('link')
        manifestLink.rel = 'manifest'
        manifestLink.href = manifestUrl
        document.head.appendChild(manifestLink)
      }
      
      console.log('📱 PWA Manifest atualizado dinamicamente:', manifest)
    } catch (error) {
      console.error('Erro ao atualizar manifesto PWA:', error)
    }
  }

  const installPWA = async () => {
    try {
      // @ts-ignore - beforeinstallprompt não está tipado
      if (window.deferredPrompt) {
        // @ts-ignore
        window.deferredPrompt.prompt()
        // @ts-ignore
        const { outcome } = await window.deferredPrompt.userChoice
        console.log('PWA install outcome:', outcome)
        // @ts-ignore
        window.deferredPrompt = null
        return outcome === 'accepted'
      }
      return false
    } catch (error) {
      console.error('Erro ao instalar PWA:', error)
      return false
    }
  }

  const isInstallable = () => {
    // @ts-ignore
    return Boolean(window.deferredPrompt)
  }

  const isInstalled = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           // @ts-ignore
           window.navigator.standalone === true
  }

  return {
    church,
    loading,
    updateManifest,
    installPWA,
    isInstallable: isInstallable(),
    isInstalled: isInstalled(),
    manifest: generatePWAManifest(church)
  }
}

// Adicionar listener para o evento beforeinstallprompt
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevenir o prompt automático
    e.preventDefault()
    // Salvar o evento para uso posterior
    // @ts-ignore
    window.deferredPrompt = e
    console.log('PWA install prompt disponível')
  })

  window.addEventListener('appinstalled', () => {
    console.log('PWA foi instalado')
    // @ts-ignore
    window.deferredPrompt = null
  })
}